import { Button, Color, Statistic } from '@bb/elements-web';
import { getOverdueBalance } from 'api/SupraController';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { formatCurrency } from 'utils';
import AuthContext from 'contexts/AuthContext';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import { useAnsweringServiceDetails } from 'hooks';
import { ViewingLevel } from 'Router/pages/SubscriptionsPaymentsPage';
import { OverdueBalanceType } from '../../../../SubscriptionsPageContext';
import { SUBSCRIPTION_UPDATED_EVENT } from '../../../../../BillingInvoicesPage/components/BalanceCard';

/**
 * Formats a date string to "MMM D, YYYY" format (e.g., "Mar 20, 2025")
 */
export const formatBillingDate = (date?: string | Date): string => {
  if (!date) return '-';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return typeof date === 'string' ? date : '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(dateObj);
};

interface BillingCardProps {
  /** Whether these are the subscriptions for an indivdual user or for a brokerage paying for all its users */
  viewingLevel: ViewingLevel;
  /** Does the current user have an overdue balance on any of their subscriptions */
  isSubscriptionOverdue: OverdueBalanceType;
  /** The organization ID */
  orgId?: string;
  /** The billing customer number */
  billingCustomerNumber?: string;
}

/**
 * Displays billing information for the Payment card.
 */
const BillingCard = (props: BillingCardProps) => {
  const { auth } = useContext(AuthContext);

  const [outstandingBalance, setOutstandingBalance] = useState<number | null>(
    null,
  );
  const [subscriptionPeriodEnd, setSubscriptionPeriodEnd] =
    useState<Date | null>(null);
  const [hasSubscriptions, setHasSubscriptions] = useState(false);

  const { viewingLevel, billingCustomerNumber, isSubscriptionOverdue } = props;

  const hasActiveSupraSystemSubscription = useCallback(async () => {
    if (
      !!auth.supraSubscription &&
      auth.supraSubscription.status !==
        SupraSystemSubscriptionStatus.unsubscribed
    ) {
      setHasSubscriptions(true);
    }
  }, [auth.supraSubscription]);

  const {
    answeringServicePayer,
    answeringServiceType,
    hasActiveAnsweringService,
  } = useAnsweringServiceDetails({
    viewingLevel,
    userId: auth.id,
  });
  const isPayingForOwnSub =
    (answeringServicePayer === 'brokerage' && viewingLevel === 'org') ||
    (answeringServicePayer === 'user' && viewingLevel === 'user');
  /** Whether this user/brokerage currently has an ongoing AS subscription they are actively paying for. */
  const hasActiveAnsweringServiceSubscription = useCallback(async () => {
    if (
      hasActiveAnsweringService &&
      isPayingForOwnSub &&
      answeringServiceType !== 'Legacy'
    ) {
      setHasSubscriptions(true);
    }
  }, [hasActiveAnsweringService, isPayingForOwnSub, answeringServiceType]);

  // TODO KB-1686 - Check if there are any active Tier 2 org plan subscriptions
  useEffect(() => {
    hasActiveSupraSystemSubscription();
    hasActiveAnsweringServiceSubscription();
  }, [hasActiveSupraSystemSubscription, hasActiveAnsweringServiceSubscription]);

  // Fetch relevant billing information from Billing Platform
  const fetchBillingInfo = useCallback(async () => {
    // Call the API to grab the balance for an org
    if (viewingLevel === 'org') {
      // TODO KB-1686: Add implementation to fetch information for org billing (should be same as user)
      // setOutstandingBalance(balanceRes);
      // setSubscriptionPeriodEnd(subscriptionPeriodEndRes);
      setOutstandingBalance(null);
      setSubscriptionPeriodEnd(null);
    }

    // Call the API to grab the balance for a user utilizing keyholder billing
    else if (viewingLevel === 'user') {
      const apiResponse = await getOverdueBalance({
        billingCustomerNumber,
      });
      // A null response signifies the user has no outstanding balance
      if (apiResponse === null) {
        setOutstandingBalance(null);
        setSubscriptionPeriodEnd(null);
      } else {
        const {
          outstandingBalance: balanceRes,
          paymentDueDate: subscriptionPeriodEndRes,
        } = apiResponse;
        setOutstandingBalance(balanceRes);
        setSubscriptionPeriodEnd(subscriptionPeriodEndRes);
      }
    }
  }, [viewingLevel, billingCustomerNumber]);

  // Trigger fetching billing info as soon as the page loads
  useEffect(() => {
    const update = () => {
      fetchBillingInfo();
    };

    fetchBillingInfo();

    document.addEventListener(SUBSCRIPTION_UPDATED_EVENT, update);
    return () => {
      document.removeEventListener(SUBSCRIPTION_UPDATED_EVENT, update);
    };
  }, [fetchBillingInfo]); // Any time the fetchBillingInfo method changes, call this useEffect again

  // TODO add back in later in KB-1686
  // const handleClickPayNow = useCallback(() => {
  //   document.dispatchEvent(new Event(SHOW_UPDATE_CARD_EVENT(auth.id)));
  // }, [auth.id]);

  let formattedOutstandingBalance;
  if (
    (outstandingBalance !== undefined && outstandingBalance !== null) ||
    subscriptionPeriodEnd
  ) {
    formattedOutstandingBalance = formatCurrency(Number(outstandingBalance), {
      minimumFractionDigits: 2,
    });
  } else formattedOutstandingBalance = '-';

  const effectiveDate = subscriptionPeriodEnd
    ? new Date(subscriptionPeriodEnd)
    : null;
  const formattedDate = formatBillingDate(effectiveDate);

  if (!hasSubscriptions) {
    return <BillingText>You have no paid subscriptions.</BillingText>;
  }

  if (isSubscriptionOverdue.response) {
    return (
      <StyledBody>
        <StyledSection>
          <StyledBalanceText value={formattedOutstandingBalance} />
          <BillingText>
            You must pay your balance by {formattedDate} or your paid
            subscriptions will be suspended.
          </BillingText>
          {/* TODO add back in
          {isSubscriptionOverdue.response && (
            <StyledButton
              dataCy={'pay-now-button'}
              size="small"
              onClick={handleClickPayNow}
            >
              Pay Now
            </StyledButton>
          )} */}
        </StyledSection>
      </StyledBody>
    );
  }

  return (
    <BillingText>Your subscriptions will renew on {formattedDate}.</BillingText>
  );
};

const BillingText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #1a1b21;
`;

const StyledBody = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  grid-row-gap: 25px;
  grid-column-gap: 25px;
`;

const StyledSection = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  border: 1px solid ${Color.LIGHTER_GREY};
  border-radius: 4px;
  padding: 16px;
  align-items: center;
`;

const StyledBalanceText = styled(Statistic)`
  margin-right: 24px;
  .ant-statistic-content {
    color: ${Color.DODORIA};
  }
`;

const StyledButton = styled(Button)`
  &&& {
    width: 98px;
    min-width: 98px;
  }

  margin-top: 8px;
  margin-bottom: 8px;
  margin-left: auto;
`;

export default BillingCard;
